import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 * Only activates if VITE_SENTRY_DSN is configured in environment variables
 */
export function initializeSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Skip initialization if no DSN is configured
  if (!dsn) {
    console.info('Sentry disabled (no DSN configured)');
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
      tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),

      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Session Replay sampling rates
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      // Filter and sanitize events before sending
      beforeSend(event, hint) {
        // Filter out sensitive data
        if (event.request) {
          // Remove query parameters that might contain sensitive data
          if (event.request.url) {
            try {
              const url = new URL(event.request.url);
              // Remove potentially sensitive query parameters
              ['token', 'key', 'password', 'secret', 'api_key'].forEach(param => {
                url.searchParams.delete(param);
              });
              event.request.url = url.toString();
            } catch (e) {
              // Invalid URL, keep as is
            }
          }

          // Remove sensitive headers
          if (event.request.headers) {
            delete event.request.headers['Authorization'];
            delete event.request.headers['Cookie'];
          }
        }

        // Sanitize user data
        if (event.user) {
          // Keep only non-sensitive user data
          const sanitizedUser: Sentry.User = {
            id: event.user.id,
          };
          event.user = sanitizedUser;
        }

        // Filter PII from event data
        if (event.extra) {
          event.extra = sanitizeObject(event.extra);
        }

        if (event.contexts) {
          event.contexts = sanitizeObject(event.contexts);
        }

        return event;
      },
    });

    console.info('Sentry initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
}

/**
 * Recursively sanitize an object to remove PII
 */
function sanitizeObject(obj: any): any {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const sanitized: any = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      // Skip sensitive keys
      if (isSensitiveKey(key)) {
        sanitized[key] = '[REDACTED]';
        continue;
      }

      // Recursively sanitize nested objects
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        sanitized[key] = sanitizeObject(obj[key]);
      } else if (typeof obj[key] === 'string') {
        // Sanitize strings that might contain PII
        sanitized[key] = sanitizeString(obj[key]);
      } else {
        sanitized[key] = obj[key];
      }
    }
  }

  return sanitized;
}

/**
 * Check if a key name suggests it contains sensitive data
 */
function isSensitiveKey(key: string): boolean {
  const sensitivePatterns = [
    /password/i,
    /token/i,
    /secret/i,
    /api[_-]?key/i,
    /auth/i,
    /credit[_-]?card/i,
    /ssn/i,
    /social[_-]?security/i,
    /pin/i,
  ];

  return sensitivePatterns.some(pattern => pattern.test(key));
}

/**
 * Sanitize strings that might contain PII (emails, phone numbers, etc.)
 */
function sanitizeString(str: string): string {
  // Redact email addresses
  str = str.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL]');

  // Redact phone numbers (various formats)
  str = str.replace(/(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, '[PHONE]');

  return str;
}
